import { useState, useEffect } from "react";

// New unified API (grotron-api NEW_API_1). After a successful payment, PayU sends
// the browser here (via fm_payment_return -> 303) with ?txnid=. We register the
// paid person to Zoom by that txnid (the webhook has already recorded the paid
// registration as SUCCESS, so this just creates the actual Zoom registrant).
const API_BASE =
  "https://api.grotron.farm/01b0efcc-69b3-4b7b-9802-8a6201f93462";

export default function PaymentSuccess() {
  const [status, setStatus] = useState("loading"); // loading | done | noref
  const [meeting, setMeeting] = useState(null);
  const [joinUrl, setJoinUrl] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const txnid = params.get("txnid");
      if (!txnid) {
        setStatus("noref");
        return;
      }

      // 1) Register the paid person to Zoom (best-effort — payment already succeeded).
      try {
        const regRes = await fetch(`${API_BASE}/fm_zoom_register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ txnid }),
        });
        const regData = await regRes.json();
        if (regData && regData.join_url) {
          setJoinUrl(regData.join_url);
        } else if (regData && regData.error) {
          // Payment is fine; Zoom registration hit an issue (e.g. already
          // registered, or the meeting id needs updating). Keep it reassuring.
          setNote(
            "Your payment was received. If you don't get the joining email shortly, please contact the organizer."
          );
        }
      } catch (err) {
        console.error(err);
        setNote(
          "Your payment was received. If you don't get the joining email shortly, please contact the organizer."
        );
      }

      // 2) Load meeting details for display (best-effort).
      try {
        const mRes = await fetch(`${API_BASE}/fm_zoom_details`);
        const mData = await mRes.json();
        if (mData && !mData.error && mData.topic) setMeeting(mData);
      } catch (err) {
        /* ignore */
      }

      setStatus("done");
    };
    run();
  }, []);

  if (status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
        }}
      >
        <p style={{ color: "#555", fontSize: 18 }}>
          Confirming your registration...
        </p>
      </div>
    );
  }

  if (status === "noref") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
          textAlign: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: 40,
            borderRadius: 20,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            maxWidth: 500,
          }}
        >
          <h1 style={{ fontSize: 24, color: "#555" }}>No payment reference found</h1>
          <p style={{ fontSize: 16, color: "#555" }}>
            If you completed a payment, please contact the organizer.
          </p>
        </div>
      </div>
    );
  }

  // ✅ Success
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
        padding: 20,
        textAlign: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: 40,
          borderRadius: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          maxWidth: 500,
        }}
      >
        <h1 style={{ fontSize: 32, color: "#28a745", marginBottom: 20 }}>
          ✅ Payment successful — you're registered!
        </h1>

        {meeting && (
          <p style={{ fontSize: 16, color: "#555", marginTop: 10 }}>
            <strong>Topic:</strong> "{meeting.topic}"
            <br />
            <strong>Time:</strong>{" "}
            {new Date(meeting.start_time).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
            <br />
            <strong>Duration:</strong> {meeting.duration || "N/A"} mins
            <br />
            <strong>Host:</strong> {meeting?.settings?.contact_name || "N/A"}
          </p>
        )}

        <p style={{ fontSize: 16, color: "#555", marginTop: 20 }}>
          Joining information has been sent to your email.
        </p>

        {joinUrl && (
          <p style={{ marginTop: 20 }}>
            <a
              href={joinUrl}
              style={{
                display: "inline-block",
                padding: "12px 26px",
                borderRadius: 10,
                background: "linear-gradient(90deg,#4f46e5,#6366f1)",
                color: "#fff",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              Join the webinar
            </a>
          </p>
        )}

        {note && (
          <p style={{ fontSize: 14, color: "#888", marginTop: 20 }}>{note}</p>
        )}
      </div>
    </div>
  );
}
